// EmailTemplates.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import MaterialTable, { MTableToolbar } from "material-table";
import { Tooltip } from "react-tippy";
import "react-tippy/dist/tippy.css";
import { getTrans as t } from "../../Localization/translations";
import EmailTemplateService from "../../services/email/emailServices";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

export default function EmailTemplates() {
  const navigate = useNavigate();
  const tableRef = useRef();

  // Load saved category OR default to "all"
  const [category, setCategory] = useState(
    localStorage.getItem("emailTemplateCategory") || "all"
  );

  const [templates, setTemplates] = useState([]);

  // -----------------------------
  // Load templates (reusable)
  // -----------------------------
  const loadTemplates = async () => {
    try {
      let res;
      if (category === "all") {
        res = await EmailTemplateService.getTemplates();
      } else {
        res = await EmailTemplateService.getTemplates(category);
      }
      setTemplates(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load templates", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Initial + on category change
  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    setCategory(newCategory);
    localStorage.setItem("emailTemplateCategory", newCategory);
  };

  // -----------------------------
  // Toggle Active / Inactive
  // -----------------------------
  const handleTemplateToggle = (id, status) => {
    confirmAlert({
      title: "",
      message:
        status === "Active"
          ? "Are you sure you want to Inactivate this template?"
          : "Are you sure you want to Activate this template?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            const apiCall =
              status === "Active"
                ? EmailTemplateService.inactivateTemplate(id)
                : EmailTemplateService.activateTemplate(id);

            apiCall
              .then((response) => {
                if (response.data && response.data.success) {
                  toast.success(response.data.response || "Status updated", {
                    position: "top-right",
                    autoClose: 1000,
                  });
                  // Reload list from backend
                  loadTemplates();
                } else {
                  toast.error(
                    (response.data && response.data.error) ||
                      "Failed to update status",
                    {
                      position: "top-right",
                      autoClose: 1000,
                    }
                  );
                }
              })
              .catch((err) => {
                console.error(err);
                toast.error("Failed to update status", {
                  position: "top-right",
                  autoClose: 1000,
                });
              });
          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid pt-3">
        <div className="row">
          <div className="col-md-12">
            <div className="mb-0">
              <div className="header pb-0">
                <div className="row">
                  <div className="col-xl-9">
                    <span className="mb-3">
                      <h5>{t("Email Templates")}</h5>
                    </span>
                  </div>
                </div>

                {/* CATEGORY FILTER */}
                <div className="card">
                  <div className="card-body">
                    <div className="d-lg-flex col-md-6 row">
                      <div className="col-md-6">
                        <span className="mb-0 col-form-label">
                          {t("Template Group")}
                        </span>
                        <select
                          className="form-control mb-0"
                          value={category}
                          onChange={handleCategoryChange}
                        >
                          <option value="all">{t("All")}</option>
                          <option value="access">{t("Access")}</option>
                          <option value="notification">
                            {t("Notification")}
                          </option>
                          <option value="support">{t("Support")}</option>
                          <option value="approval">{t("Approval")}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                {/* END FILTER */}
              </div>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="body py-2">
          <div className="col-md-12">
            <MaterialTable
              tableRef={tableRef}
              style={{
                backgroundColor: "var(--bs-table-bg)",
                borderBottomWidth: "1px",
                boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
              }}
              title={<h5 className="d-lg-flex mb-0">{t("")}</h5>}
              components={{
                Toolbar: (props) => (
                  <div className="MuiToolbar-root MTableToolbar-spacer-21 my-temp-class">
                    <MTableToolbar {...props} />
                  </div>
                ),
              }}
              columns={[
                {
                  title: t("Template Name"),
                  field: "name",
                  headerStyle: { fontWeight: "bold", textAlign: "left" },
                  cellStyle: { textAlign: "left", fontSize: 12 },
                  render: (row) => (
                    <div
                      className="hyperlink"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`/view-emailtemplets?name=${row.name}`)
                      }
                    >
                      {row.name}
                    </div>
                  ),
                },
                {
                  title: t("Category"),
                  field: "category",
                  headerStyle: { fontWeight: "bold", textAlign: "left" },
                  cellStyle: { textAlign: "left", fontSize: 12 },
                  render: (row) =>
                    row.category
                      ? row.category.charAt(0).toUpperCase() +
                        row.category.slice(1)
                      : "-",
                },
                {
                  title: t("Subject"),
                  field: "subject",
                  headerStyle: { fontWeight: "bold", textAlign: "left" },
                  cellStyle: { textAlign: "left", fontSize: 12 },
                },
                {
                  title: t("Last Updated"),
                  field: "updated_at",
                  headerStyle: { fontWeight: "bold", textAlign: "left" },
                  cellStyle: { textAlign: "left", fontSize: 12 },
                  render: (row) =>
                    new Date(row.updated_at).toLocaleDateString(),
                },
                {
                  title: t("Status"),
                  field: "status",
                  headerStyle: { fontWeight: "bold", textAlign: "left" },
                  cellStyle: { textAlign: "left", fontSize: 12 },
                  render: (row) => (
                    <span>
                      {row.status || "-"}
                    </span>
                  ),
                },
                {
                  title: t("Action"),
                  field: "",
                  headerStyle: { fontWeight: "bold", textAlign: "center" },
                  cellStyle: { textAlign: "center", fontSize: 12 },
                  render: (row) => (
                    <div className="d-flex justify-content-center gap-3">
                      {/* EDIT ICON */}
                      <Tooltip title={t("Edit")} placement="top">
                        <span
                          className="cursor-pointer"
                          onClick={() =>
                            navigate(`/emailformat?name=${row.name}`)
                          }
                        >
                          <img
                            src={
                              process.env.PUBLIC_URL +
                              "/static/images/ic-edit.svg"
                            }
                            alt="edit"
                          />
                        </span>
                      </Tooltip>

                      {/* TOGGLE ACTIVE/INACTIVE */}
                      <Tooltip
                        title={
                          row.status === "Active" ? "Inactivate" : "Activate"
                        }
                        placement="top"
                      >
                        <span
                          className="cursor-pointer"
                          onClick={() =>
                            handleTemplateToggle(row.id, row.status)
                          }
                        >
                          <i
                            className="material-icons"
     
                            style={{ fontSize: "18px" }}
                          >
                            {row.status === "Active" ? "block" : "send"}
                          </i>
                        </span>
                      </Tooltip>
                    </div>
                  ),
                },
              ]}
              data={templates}
              options={{
                headerStyle: {
                  backgroundColor: "white",
                  color: "#245C7F",
                  fontSize: 12,
                },
                rowStyle: (rowData, index) => ({
                  backgroundColor: index % 2 ? "#fff" : "#f8f9fa",
                }),
                sorting: true,
                search: true,
                paging: true,
                pageSize: 10,
                pageSizeOptions: [10, 20, 25, 50],
                paginationType: "stepped",
                showFirstLastPageButtons: false,
                exportButton: true,
                exportFileName: "Email_Templates",
                columnsButton: true,
                actionsColumnIndex: -1,
              }}
            />
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}
